/// <amd-module name="Wit/WorkItemForm/History/StateGraph" />
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { Ago } from "VSSUI/Ago";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { ago, AgoFormat } from "VSSUI/Utilities/Date";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { getDateInUserTimeZone, tooltipTimeFormat } from "Wit/Common/DateUtils";
import * as Resources from "Wit/WorkItemForm/Resources";
import { FieldValue, StateCircle } from "../Common/Components";
import { IHistoryItem } from "../Common/Services";
import { getNewState, getNewValueForField, getUpdateDate } from "./HistoryUtils";

export interface IStateGraphProps {
    stateUpdates: IHistoryItem[];
}

export function StateGraph({ stateUpdates }: IStateGraphProps) {
    if (!stateUpdates.length) {
        return null;
    }

    return (
        <div className="work-item-form-history-state-graph flex-row padding-bottom-16">
            {stateUpdates.map((update, index) => {
                return <StateUpdate key={`workItem-${update.workItemId}-update-${update.id}`} stateUpdate={update} updateIndex={index} />;
            })}
        </div>
    );
}

interface IStateUpdateProps {
    stateUpdate: IHistoryItem;
    updateIndex: number
}

function StateUpdate({ stateUpdate, updateIndex }: IStateUpdateProps) {
    const newState = stateUpdate.fields[CoreFieldRefNames.State]?.newValue;
    const ariaLabel = getTransitionAriaLabel(stateUpdate, updateIndex);

    return (
        <div aria-label={ariaLabel} role="img" className="flex-row" tabIndex={0}>
            <StateTransition stateUpdate={stateUpdate} />
            <StateNode newState={newState} />
        </div>
    );
}

interface IStateNodeProps {
    newState: string;
}

function StateNode({ newState }: IStateNodeProps) {
    return (
        <div aria-hidden={true} className="state-node padding-8 white-space-nowrap">
            <StateCircle className="icon-margin" state={newState} />
            {newState}
        </div>
    );
}

interface IStateTransitionProps {
    stateUpdate: IHistoryItem;
}

function StateTransition({ stateUpdate }: IStateTransitionProps) {
    return (
        <div aria-hidden={true} className="flex-column">
            <div className="transition-top-label white-space-nowrap font-size">
                <div>
                    <TransitionReason stateUpdate={stateUpdate} />
                </div>
            </div>
            <TransitionArrow />
            <div className="transition-bottom-label white-space-nowrap font-size">
                <div className="flex-row justify-center">
                    <Persona className="icon-margin" identity={stateUpdate.revisedBy} size={PersonaSize.size16} />
                    {stateUpdate.revisedBy.displayName}
                </div>
                <div>
                    <Ago date={getUpdateDate(stateUpdate)} currentDate={getDateInUserTimeZone()} tooltipTimeFormat={tooltipTimeFormat} />
                </div>
            </div>
        </div>
    );
}

function TransitionArrow() {
    return (
        <div className="state-graph-right-arrow">
            <div className="state-graph-arrow-tail" />
            <div className="state-graph-arrow-head" />
        </div>
    );
}

function TransitionReason({ stateUpdate }: { stateUpdate: IHistoryItem }) {
    const reason = getNewValueForField(stateUpdate, CoreFieldRefNames.Reason);
    return reason ? (
        <FieldValue refName={CoreFieldRefNames.Reason} fieldValue={reason} />
    ) : (
        <span>{format(Resources.MovedToState, getNewState(stateUpdate))}</span>
    );
}

function getTransitionAriaLabel(stateUpdate: IHistoryItem, updateIndex: number): string {
    const transitionIndex = updateIndex + 1;
    const newState = stateUpdate.fields[CoreFieldRefNames.State]?.newValue;
    const reason = getNewValueForField(stateUpdate, CoreFieldRefNames.Reason);
    const revisedBy = stateUpdate.revisedBy.displayName;
    const transitionDate = ago(getUpdateDate(stateUpdate), AgoFormat.Compact);
    return format(Resources.StateTransitionAriaLabel, transitionIndex, newState, reason, revisedBy, transitionDate);
}
